import { unionBy } from "lodash-es";
import { createReducer } from "typesafe-actions";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import EmailDraft from "@mapmycustomers/shared/types/draft/EmailDraft";
import Email from "@mapmycustomers/shared/types/email/Email";
import EmailQuota from "@mapmycustomers/shared/types/email/EmailQuota";
import EmailSyncProgress from "@mapmycustomers/shared/types/email/EmailSyncProgress";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import { EntitiesSupportedByEmailFeature, Resource } from "@mapmycustomers/shared/types/entity";

import { UiSyncActions } from "@app/store/uiSync";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import applyEmailObjectsChanges from "@app/util/email/applyEmailObjectsChanges";
import getEmailMetadataResourcesIds from "@app/util/email/getEmailMetadataResourcesIds";

import {
  Actions,
  createEmailDraft,
  createEmailTemplate,
  deleteEmailDraft,
  deleteEmailTemplate,
  fetchEmailDrafts,
  fetchEmailQueue,
  fetchEmailQuota,
  fetchEmailSyncProgress,
  fetchEmailTemplates,
  hideEmailFeatures,
  hideEmailQueue,
  shareEmailTemplate,
  showEmailFeatures,
  showEmailQueue,
  updateEmailDraft,
  updateEmailTemplate,
} from "./actions";

export interface EmailState {
  drafts: EmailDraft[];
  draftsEntities: EntitiesSupportedByEmailFeature[];
  draftsLoaded: boolean;
  draftsResources: Resource[];
  emailFeaturesVisible: boolean;
  emailQueue: Email[];
  emailQueueLoading: boolean;
  emailQueueVisible: boolean;
  emailQuota?: EmailQuota;
  emailQuotaLoading: boolean;
  emailSyncProgress?: EmailSyncProgress;
  templateCreating: boolean;
  templateDeleting: boolean;
  templates: EmailTemplate[];
  templatesLoading: boolean;
  templatesResources: Resource[];
  templateUpdating: boolean;
}

const initialState: EmailState = {
  drafts: [],
  draftsEntities: [],
  draftsLoaded: false,
  draftsResources: [],
  emailFeaturesVisible: false,
  emailQueue: [],
  emailQueueLoading: false,
  emailQueueVisible: false,
  emailQuotaLoading: false,
  templateCreating: false,
  templateDeleting: false,
  templates: [],
  templatesLoading: false,
  templatesResources: [],
  templateUpdating: false,
};

const cleanupStoreObjects = <T extends Identified>(
  storeObjects: T[],
  usedObjectIds: T["id"][]
): T[] => {
  return storeObjects.filter((storeObject) => usedObjectIds.includes(storeObject.id));
};

const updateStoreObjects = <T extends Identified>(storeObjects: T[], updatedObjects: T[]): T[] => {
  return unionBy(updatedObjects, storeObjects, (object) => object.id);
};

const getDraftsMetadataEntityIds = (drafts: EmailDraft[]) =>
  drafts.flatMap((draft) => draft.metadata.recipients.to.map((recipient) => recipient.id));

const email = createReducer<EmailState, Actions | UiSyncActions>(initialState)
  .handleAction(fetchEmailQuota.request, (state) => ({
    ...state,
    emailQuotaLoading: true,
  }))
  .handleAction(fetchEmailQuota.success, (state, { payload }) => ({
    ...state,
    emailQuota: payload,
    emailQuotaLoading: false,
  }))
  .handleAction(fetchEmailQuota.failure, (state) => ({
    ...state,
    emailQuotaLoading: false,
  }))
  .handleAction(fetchEmailSyncProgress.request, (state) => ({
    ...state,
  }))
  .handleAction(fetchEmailSyncProgress.success, (state, { payload }) => ({
    ...state,
    emailSyncProgress: payload,
  }))
  .handleAction(fetchEmailSyncProgress.failure, (state) => ({
    ...state,
  }))
  .handleAction(fetchEmailQueue.request, (state) => ({
    ...state,
    emailQueueLoading: true,
  }))
  .handleAction(fetchEmailQueue.success, (state, { payload }) => ({
    ...state,
    emailQueue: payload,
    emailQueueLoading: false,
  }))
  .handleAction(fetchEmailQueue.failure, (state) => ({
    ...state,
    emailQueueLoading: false,
  }))
  .handleAction(fetchEmailTemplates.request, (state) => ({
    ...state,
    templatesLoading: true,
  }))
  .handleAction(fetchEmailTemplates.success, (state, { payload: { resources, templates } }) => ({
    ...state,
    templates,
    templatesLoading: false,
    templatesResources: resources,
  }))
  .handleAction(fetchEmailTemplates.failure, (state) => ({
    ...state,
    templatesLoading: false,
  }))
  .handleAction(createEmailTemplate.request, (state) => ({
    ...state,
    templateCreating: true,
  }))
  .handleAction(createEmailTemplate.success, (state, { payload }) => {
    const templates = [...state.templates, payload.template];
    return {
      ...state,
      templateCreating: false,
      templates,
      templatesResources: cleanupStoreObjects(
        updateStoreObjects(state.templatesResources, payload.resources),
        getEmailMetadataResourcesIds(templates)
      ),
    };
  })
  .handleAction(createEmailTemplate.failure, (state) => ({
    ...state,
    templateCreating: false,
  }))
  .handleAction(updateEmailTemplate.request, (state) => ({
    ...state,
    templateUpdating: true,
  }))
  .handleAction(updateEmailTemplate.success, (state, { payload }) => {
    const templates = state.templates.map((template) =>
      template.id === payload.template.id ? payload.template : template
    );
    return {
      ...state,
      templates,
      templatesResources: payload.resources
        ? cleanupStoreObjects(
            updateStoreObjects(state.templatesResources, payload.resources),
            getEmailMetadataResourcesIds(templates)
          )
        : state.templatesResources,
      templateUpdating: false,
    };
  })
  .handleAction(updateEmailTemplate.failure, (state) => ({
    ...state,
    templateUpdating: false,
  }))
  .handleAction(deleteEmailTemplate.request, (state) => ({
    ...state,
    templateDeleting: true,
  }))
  .handleAction(deleteEmailTemplate.success, (state, { payload }) => {
    const templates = state.templates.filter((template) => template.id !== payload);
    return {
      ...state,
      templateDeleting: false,
      templates,
      templatesResources: cleanupStoreObjects(
        state.templatesResources,
        getEmailMetadataResourcesIds(templates)
      ),
    };
  })
  .handleAction(deleteEmailTemplate.failure, (state) => ({
    ...state,
    templateDeleting: false,
  }))
  .handleAction(shareEmailTemplate.success, (state, { payload }) => ({
    ...state,
    templates: state.templates.map((template) =>
      template.id === payload.templateId ? { ...template, userIds: payload.userIds } : template
    ),
  }))
  .handleAction(showEmailQueue, (state) => ({
    ...state,
    emailQueueVisible: true,
  }))
  .handleAction(hideEmailQueue, (state) => ({
    ...state,
    emailQueueVisible: false,
  }))
  .handleAction(showEmailFeatures, (state) => ({
    ...state,
    emailFeaturesVisible: true,
  }))
  .handleAction(hideEmailFeatures, (state) => ({
    ...state,
    emailFeaturesVisible: false,
  }))
  .handleAction(fetchEmailDrafts.request, (state) => ({
    ...state,
  }))
  .handleAction(
    fetchEmailDrafts.success,
    (state, { payload: { drafts, entities, resources } }) => ({
      ...state,
      drafts,
      draftsEntities: entities,
      draftsLoaded: true,
      draftsResources: resources,
    })
  )
  .handleAction(createEmailDraft.success, (state, { payload }) => {
    const drafts = [...state.drafts, payload.draft];
    return {
      ...state,
      drafts,
      draftsEntities: cleanupStoreObjects(
        updateStoreObjects(state.draftsEntities, payload.entities),
        getDraftsMetadataEntityIds(drafts)
      ),
      draftsResources: cleanupStoreObjects(
        updateStoreObjects(state.draftsResources, payload.resources),
        getEmailMetadataResourcesIds(drafts)
      ),
    };
  })
  .handleAction(updateEmailDraft.success, (state, { payload }) => {
    const drafts = state.drafts.map((template) =>
      template.id === payload.draft.id ? payload.draft : template
    );
    return {
      ...state,
      drafts,
      draftsEntities: cleanupStoreObjects(
        updateStoreObjects(state.draftsEntities, payload.entities),
        getDraftsMetadataEntityIds(drafts)
      ),
      draftsResources: cleanupStoreObjects(
        updateStoreObjects(state.draftsResources, payload.resources),
        getEmailMetadataResourcesIds(drafts)
      ),
    };
  })
  .handleAction(deleteEmailDraft.request, (state, { payload }) => ({
    ...state,
    drafts: state.drafts.filter((draft) => draft.id !== payload.draft.id),
  }))
  .handleAction(deleteEmailDraft.success, (state) => {
    return {
      ...state,
      draftsEntities: cleanupStoreObjects(
        state.draftsEntities,
        getDraftsMetadataEntityIds(state.drafts)
      ),
      draftsResources: cleanupStoreObjects(
        state.draftsResources,
        getEmailMetadataResourcesIds(state.drafts)
      ),
    };
  })
  .handleAction(deleteEmailDraft.failure, (state, { payload }) => ({
    ...state,
    drafts: [...state.drafts, payload],
  }))
  .handleAction(notifyAboutChanges, (state, { payload }) => {
    return {
      ...state,
      draftsEntities: applyEmailObjectsChanges({
        changedObjectsType: payload.entityType,
        deletedObjectIds: payload.deletedIds,
        objects: state.draftsEntities,
        objectsTypes: [EntityType.COMPANY, EntityType.PERSON],
        updatedObjects: payload.updated,
      }),
      draftsResources: applyEmailObjectsChanges({
        changedObjectsType: payload.entityType,
        deletedObjectIds: payload.deletedIds,
        objects: state.draftsResources,
        objectsTypes: [EntityType.RESOURCE],
        restoredObjects: payload.restored,
        updatedObjects: payload.updated,
        usingObjectIds: state.drafts.flatMap((draft) =>
          draft.metadata.resources.map((resource) => resource.id)
        ),
      }),
      templatesResources: applyEmailObjectsChanges({
        changedObjectsType: payload.entityType,
        deletedObjectIds: payload.deletedIds,
        objects: state.templatesResources,
        objectsTypes: [EntityType.RESOURCE],
        restoredObjects: payload.restored,
        updatedObjects: payload.updated,
        usingObjectIds: state.templates.flatMap(
          (template) => template.metadata?.resources?.map((resource) => resource.id) ?? []
        ),
      }),
    };
  });

export * from "./selectors";
export type EmailActions = Actions;

export default email;
